import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, IconButton, useToast, ConfirmDialog, Modal } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { RateFactor, CreateRateFactorInput } from '../../../shared/services/humanResources/types';

const repo = getHumanResourcesRepository();

const emptyForm: CreateRateFactorInput = {
  effectiveFrom: '',
  effectiveTo: null,
  monthlyHoursDivisor: 210,
  nightStartTime: '19:00',
  nightEndTime: '06:00',
  extraDiurnalFactor: 1.25,
  extraNocturnalFactor: 1.75,
  extraDiurnalFestiveFactor: 2.15,
  extraNocturnalFestiveFactor: 2.65,
  nightSurchargeFactor: 0.35,
  nightFestiveWithCompFactor: 1.25,
  nightFestiveWithoutCompFactor: 2.25,
  festiveWithCompFactor: 0.9,
  festiveWithoutCompFactor: 1.9,
  legalReference: '',
};

function fmt(n: number): string {
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * CRUD de Factores Legales (Configuración > Parámetros > Factores Legales).
 * Equivalente a la fila 3 de RESUMEN del Excel (multiplicadores quemados en
 * celdas) - aquí quedan versionados por fecha de vigencia y editables desde
 * la UI, sin necesitar un script SQL cada vez que la ley cambia.
 */
export function RateFactorsTab() {
  const toast = useToast();
  const [factors, setFactors] = useState<RateFactor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<RateFactor | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateRateFactorInput>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<RateFactor | null>(null);

  const load = () => repo.listRateFactors().then((f) => { setFactors(f); setIsLoading(false); });
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setCreating(true);
  };
  const openEdit = (f: RateFactor) => {
    setForm({ ...f });
    setEditing(f);
  };
  const closeModal = () => {
    setCreating(false);
    setEditing(null);
  };

  const save = async () => {
    if (!form.effectiveFrom) {
      toast.show('La fecha de inicio de vigencia es obligatoria.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateRateFactor(editing.id, form);
        toast.show('Factor actualizado.');
      } else {
        await repo.createRateFactor(form);
        toast.show('Factor creado.');
      }
      closeModal();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar el factor.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns: Column<RateFactor>[] = [
    { key: 'vigencia', header: 'Vigencia', render: (f) => `${f.effectiveFrom} → ${f.effectiveTo ?? 'hoy'}`, sortable: true, sortValue: (f) => f.effectiveFrom },
    { key: 'divisor', header: 'Divisor', render: (f) => fmt(f.monthlyHoursDivisor) },
    { key: 'nocturno', header: 'Horario nocturno', render: (f) => `${f.nightStartTime} - ${f.nightEndTime}` },
    { key: 'hedo', header: 'HEDO', render: (f) => fmt(f.extraDiurnalFactor) },
    { key: 'heno', header: 'HENO', render: (f) => fmt(f.extraNocturnalFactor) },
    { key: 'hedf', header: 'HEDF', render: (f) => fmt(f.extraDiurnalFestiveFactor) },
    { key: 'henf', header: 'HENF', render: (f) => fmt(f.extraNocturnalFestiveFactor) },
    { key: 'rno', header: 'RNO', render: (f) => fmt(f.nightSurchargeFactor) },
    { key: 'rnfcc', header: 'RNFCC', render: (f) => fmt(f.nightFestiveWithCompFactor) },
    { key: 'rnfsc', header: 'RNFSC', render: (f) => fmt(f.nightFestiveWithoutCompFactor) },
    { key: 'fcc', header: 'FCC', render: (f) => fmt(f.festiveWithCompFactor) },
    { key: 'fsc', header: 'FSC', render: (f) => fmt(f.festiveWithoutCompFactor) },
    { key: 'ref', header: 'Referencia legal', render: (f) => f.legalReference ?? '—' },
    {
      key: 'actions',
      header: '',
      render: (f) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(f)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(f)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-400">
        HEDO/HENO/HEDF/HENF: hora extra diurna/nocturna ordinaria/festiva. RNO/RNFCC/RNFSC: recargo nocturno ordinario / festivo con·sin compensatorio. FCC/FSC: festivo
        con·sin compensatorio. Ver Documentación_Macros_CALCULO_TIEMPO_EXTRA.md para el detalle de cada sigla.
      </p>
      <Button size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Nuevo periodo de vigencia
      </Button>
      <DataTable columns={columns} rows={factors} rowKey={(f) => f.id} isLoading={isLoading} emptyTitle="Sin factores legales cargados" />

      <Modal open={creating || !!editing} onClose={closeModal} title={editing ? 'Editar factor legal' : 'Nuevo factor legal'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Vigente desde" type="date" value={form.effectiveFrom} onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))} />
            <Input label="Vigente hasta (vacío = hoy)" type="date" value={form.effectiveTo ?? ''} onChange={(e) => setForm((f) => ({ ...f, effectiveTo: e.target.value || null }))} />
          </div>
          <Input label="Divisor de horas mensuales" type="number" value={form.monthlyHoursDivisor} onChange={(e) => setForm((f) => ({ ...f, monthlyHoursDivisor: Number(e.target.value) }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Inicio horario nocturno" type="time" value={form.nightStartTime} onChange={(e) => setForm((f) => ({ ...f, nightStartTime: e.target.value }))} />
            <Input label="Fin horario nocturno" type="time" value={form.nightEndTime} onChange={(e) => setForm((f) => ({ ...f, nightEndTime: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="HEDO (extra diurna ord.)" type="number" step="0.001" value={form.extraDiurnalFactor} onChange={(e) => setForm((f) => ({ ...f, extraDiurnalFactor: Number(e.target.value) }))} />
            <Input label="HENO (extra nocturna ord.)" type="number" step="0.001" value={form.extraNocturnalFactor} onChange={(e) => setForm((f) => ({ ...f, extraNocturnalFactor: Number(e.target.value) }))} />
            <Input label="HEDF (extra diurna fest.)" type="number" step="0.001" value={form.extraDiurnalFestiveFactor} onChange={(e) => setForm((f) => ({ ...f, extraDiurnalFestiveFactor: Number(e.target.value) }))} />
            <Input label="HENF (extra nocturna fest.)" type="number" step="0.001" value={form.extraNocturnalFestiveFactor} onChange={(e) => setForm((f) => ({ ...f, extraNocturnalFestiveFactor: Number(e.target.value) }))} />
            <Input label="RNO (recargo noct. ord.)" type="number" step="0.001" value={form.nightSurchargeFactor} onChange={(e) => setForm((f) => ({ ...f, nightSurchargeFactor: Number(e.target.value) }))} />
            <Input label="RNFCC (noct. fest. c/comp.)" type="number" step="0.001" value={form.nightFestiveWithCompFactor} onChange={(e) => setForm((f) => ({ ...f, nightFestiveWithCompFactor: Number(e.target.value) }))} />
            <Input label="RNFSC (noct. fest. s/comp.)" type="number" step="0.001" value={form.nightFestiveWithoutCompFactor} onChange={(e) => setForm((f) => ({ ...f, nightFestiveWithoutCompFactor: Number(e.target.value) }))} />
            <Input label="FCC (festivo c/comp.)" type="number" step="0.001" value={form.festiveWithCompFactor} onChange={(e) => setForm((f) => ({ ...f, festiveWithCompFactor: Number(e.target.value) }))} />
            <Input label="FSC (festivo s/comp.)" type="number" step="0.001" value={form.festiveWithoutCompFactor} onChange={(e) => setForm((f) => ({ ...f, festiveWithoutCompFactor: Number(e.target.value) }))} />
          </div>
          <Input label="Referencia legal (opcional)" value={form.legalReference ?? ''} onChange={(e) => setForm((f) => ({ ...f, legalReference: e.target.value }))} placeholder="Ej. Ley 2101 de 2021 + Ley 2466 de 2025" />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={closeModal}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar factor legal"
        description={`¿Eliminar el periodo de vigencia desde ${deleting?.effectiveFrom}? Los turnos ya liquidados con este factor no cambian retroactivamente.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteRateFactor(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
